import { filter, groupBy, mapValues, reduce, min } from 'lodash/fp';
import { compose } from 'redux';
import { createSelector } from 'reselect';
import { keyBy } from '@mews/fp';
import {
    AppClockService,
    DateTime,
    DateTimeService,
    DateTimeUtc,
    DateTimeUtcService,
    IntervalType,
    Interval as IntervalService,
    FeatureFlagService,
} from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { createHandler } from '@mews/std';
import { Reservation, ServiceOrderState, SimpleResourceUseResult } from '@mews-commander/data-access';
import {
    ApplicationState,
    Option,
    Interval,
    ResourceBlock,
    SimpleDataReservation,
    WithTimeUnit,
} from '@mews-commander/types';
import { Resource } from '@mews-commander/resources';
import { FeatureFlag } from '@mews-commander/enums';
import { getDividedDateRangeByTimeUnit, getIntervalUnitLength } from '@mews-commander/utils';
import { getCurrentEnterpriseServiceOptions } from '@mews-commander/legacy-entities/src/selectors/services';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { getFlatTimelineResourceUses } from '../reducers';
import { TimelineService } from '../timelineService';
import { getSelectedTimeUnit } from '../selectors/view';
import { TimelineBlock } from '../types';
import { HOUSE_USE_MONTHLY_VIEW_LIMIT } from '../constants';
import {
    DateAndTimeStrings,
    calculateTimelineResourceUseTimeState,
    convertResourceStateToServerEnum,
    convertResourceTimeStateToServerEnum,
} from './resourceUseTimeState';
import { intervalToUTC } from './timeUnit';

interface TotalStay {
    guestId: string;
    analytics: {
        totalStays?: number | null;
    };
}
interface ReservationsPayload {
    reservations: Array<Reservation>;
    resourceBlocks?: Array<ResourceBlock>;
}

interface TimelineResourceUseExtendersData extends WithTimeUnit {
    currentDateAndTime: DateAndTimeStrings;
    isReservation: boolean;
    serviceOptions?: Record<string, Option>;
    state: ApplicationState;
    timezone: string;
    totalStays?: Record<string, TotalStay> | null;
}

const getTimelineResourceUseExtendersData = createSelector(
    getCurrentEnterpriseAppDateTimeNow,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getSelectedTimeUnit,
    getCurrentEnterpriseServiceOptions,
    (nowUtcDateTime, timezone: string, timeUnit, serviceOptions): TimelineResourceUseExtendersData => {
        const startLocalDay = DateTimeService.startOfDay(nowUtcDateTime);
        const endLocalDay = DateTimeService.endOfDay(nowUtcDateTime);
        const currentDateAndTime = {
            nowUtc: DateTimeService.toUtc(nowUtcDateTime).value,
            localDay: {
                startUtc: DateTimeService.toUtc(startLocalDay).value,
                endUtc: DateTimeService.toUtc(endLocalDay).value,
            },
        };

        return {
            currentDateAndTime,
            // @ts-expect-error $TSFixMeError: Type 'any[]' is not assignable to type 'Record<string, Option>'.
            serviceOptions,
            TimelineService,
            timeUnit,
            timezone,
        };
    }
);

export const transformSimpleResourceUsesPayload = (
    { reservationData, resourceBlocks }: SimpleResourceUseResult,
    getState: () => ApplicationState,
    appClockService: AppClockService,
    featureFlagService: FeatureFlagService
) => {
    const state = getState();
    const timelineResourceUses = getFlatTimelineResourceUses(state);
    const timelineResourceUseExtendersData = getTimelineResourceUseExtendersData(state, { appClockService });

    const reservations = mapSimpleDateToTimelineResourceUse({
        ...timelineResourceUseExtendersData,
        isReservation: true,
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        serviceOptions: keyBy('value', timelineResourceUseExtendersData.serviceOptions),
        state,
    })(reservationData) as Object;

    const timeUnit = getSelectedTimeUnit(state);

    const filteredResourceBlocks =
        timeUnit === TimeUnit.Month && featureFlagService.hasFlag(FeatureFlag.verticalLazyLoadingTimelineEnabled)
            ? resourceBlocks?.map((resourceBlock) => {
                  const hoursDifference = DateTimeUtcService.diffHours(
                      DateTimeUtcService.parse(resourceBlock.endUtc),
                      DateTimeUtcService.parse(resourceBlock.startUtc)
                  );
                  return hoursDifference >= HOUSE_USE_MONTHLY_VIEW_LIMIT
                      ? resourceBlock
                      : { ...resourceBlock, isActive: false };
              })
            : resourceBlocks;

    const blocks = mapSimpleDateToTimelineResourceUse({
        ...timelineResourceUseExtendersData,
        isReservation: false,
    })(filteredResourceBlocks) as Object;

    const extendedTimelineResourceUses = {
        ...timelineResourceUses,
        ...blocks,
        ...reservations,
    };

    return compose(
        groupBy('serviceId'),
        filter(
            ({ isReservation, state: resourceUseState, isActive }) =>
                (isReservation && resourceUseState !== ServiceOrderState.Canceled) || isActive
        )
    )(extendedTimelineResourceUses);
};

export const transformReservationsToTimelineResourceUses = (
    { reservations, resourceBlocks }: ReservationsPayload,
    getState: () => ApplicationState,
    appClockService: AppClockService
) => {
    const state = getState();
    const timelineResourceUses = getFlatTimelineResourceUses(state);
    const timelineResourceUseExtendersData = getTimelineResourceUseExtendersData(state, { appClockService });

    const transformedReservations = mapReservationToTimelineResourceUse({
        ...timelineResourceUseExtendersData,
        isReservation: true,
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        serviceOptions: keyBy('value', timelineResourceUseExtendersData.serviceOptions),
        state,
    })(reservations) as Object;

    const blocks = mapSimpleDateToTimelineResourceUse({
        ...timelineResourceUseExtendersData,
        isReservation: false,
    })(resourceBlocks) as Object;

    const extendedTimelineResourceUses = {
        ...blocks,
        ...timelineResourceUses,
        ...transformedReservations,
    };

    return compose(
        groupBy('serviceId'),
        filter(
            ({ isReservation, state: resourceUseState, isActive }) =>
                (isReservation && resourceUseState !== ServiceOrderState.Canceled) || isActive
        )
    )(extendedTimelineResourceUses);
};

const mapSimpleDateToTimelineResourceUse = ({
    currentDateAndTime,
    isReservation,
    serviceOptions,
    state,
    timeUnit,
    timezone,
}: TimelineResourceUseExtendersData) =>
    compose(
        mapValues((simpleResourceUse: SimpleDataReservation) =>
            extendSimpleResourceUse({
                currentDateAndTime,
                isReservation,
                serviceOptions,
                simpleResourceUse,
                state,
                timeUnit,
                timezone,
            })
        ),
        keyBy('id')
    );

const mapReservationToTimelineResourceUse = ({
    currentDateAndTime,
    isReservation,
    serviceOptions,
    state,
    timeUnit,
    timezone,
}: TimelineResourceUseExtendersData) =>
    compose(
        mapValues((reservation: Reservation) =>
            extendReservation({
                currentDateAndTime,
                isReservation,
                reservation,
                serviceOptions,
                state,
                timeUnit,
                timezone,
            })
        ),
        keyBy('id')
    );

const getTimeStateDataFromSimpleResourceUse = (
    simpleResourceUse: SimpleDataReservation,
    currentDateAndTime: DateAndTimeStrings
) => {
    const reservationTimeState = calculateTimelineResourceUseTimeState(simpleResourceUse, currentDateAndTime);
    const state = convertResourceStateToServerEnum(simpleResourceUse.serviceOrderState)?.value;
    const timeState = convertResourceTimeStateToServerEnum(reservationTimeState)?.value;

    return {
        reservationTimeState,
        state,
        timeState,
    };
};

const extendSimpleResourceUse = ({
    currentDateAndTime,
    isReservation,
    serviceOptions,
    simpleResourceUse,
    state,
    timeUnit,
    timezone,
}: TimelineResourceUseExtendersData & { simpleResourceUse: SimpleDataReservation }) => {
    const {
        accountId,
        assignedResourceId,
        assignedResourceLocked,
        assignedSlotId,
        endUtc,
        startUtc,
        startedUtc,
        serviceId,
    } = simpleResourceUse;
    const timeStateData = isReservation
        ? getTimeStateDataFromSimpleResourceUse(simpleResourceUse, currentDateAndTime)
        : {};
    return {
        ...simpleResourceUse,
        ...timeStateData,
        assignedResourceId: assignedResourceId || assignedSlotId,
        customerId: accountId,
        displaceY: 0,
        isMoveable: isReservation && !assignedResourceLocked,
        isReservation,
        maxX: TimelineService.utcToTimeUnitIndex(endUtc, timezone, timeUnit),
        minX: TimelineService.utcToTimeUnitIndex(min([startedUtc, startUtc]) || startUtc, timezone, timeUnit),
        serviceName: serviceOptions && serviceOptions[serviceId]?.label,
        serviceTimeUnit: serviceId && getServiceTimeUnit(state, { id: serviceId }),
    };
};

const extendReservation = ({
    isReservation,
    reservation,
    serviceOptions,
    state,
    timeUnit,
    timezone,
}: TimelineResourceUseExtendersData & { reservation: Reservation }) => {
    const { assignedResourceId, assignedResourceLocked, assignedSlotId, endUtc, startUtc, serviceId } = reservation;

    return {
        ...reservation,
        assignedResourceId: assignedResourceId || assignedSlotId,
        displaceY: 0,
        isMoveable: isReservation && !assignedResourceLocked,
        isReservation,
        maxX: TimelineService.utcToTimeUnitIndex(endUtc, timezone, timeUnit),
        minX: TimelineService.utcToTimeUnitIndex(startUtc, timezone, timeUnit),
        serviceName: serviceOptions && serviceOptions[serviceId]?.label,
        serviceTimeUnit: serviceId && getServiceTimeUnit(state, { id: serviceId }),
    };
};

const timeUnitFetchLimit = createHandler({
    [TimeUnit.Month]: 8,
    [TimeUnit.Day]: 360,
    [TimeUnit.Hour]: 360,
});

export const addRequestPadding = (
    { startUtc, endUtc }: Interval,
    startPaddingFunction: (dateTime: DateTimeUtc) => DateTimeUtc,
    endPaddingFunction: (dateTime: DateTimeUtc) => DateTimeUtc
) => ({
    startUtc: startPaddingFunction(DateTimeUtcService.parse(startUtc)).value,
    endUtc: endPaddingFunction(DateTimeUtcService.parse(endUtc)).value,
});

export const chunkIntervalsIfNeeded = ({
    intervals,
    timeUnit,
    timezone,
}: {
    intervals: Interval[];
    timeUnit: TimeUnit;
    timezone: string;
}) => {
    const unitLimit = timeUnitFetchLimit(timeUnit);
    return reduce(
        (newIntervals, interval) => {
            if (getIntervalUnitLength(interval, timeUnit, timezone) > unitLimit) {
                newIntervals.push(
                    // @ts-expect-error $TSFixMeError: Argument of type '{ endUtc: string; startUtc: string; }' is not assignable to parameter of type 'never'.
                    ...getDividedDateRangeByTimeUnit({ interval, timeUnit, unitAmount: unitLimit, timezone })
                );
            } else {
                // @ts-expect-error $TSFixMeError: Argument of type 'Interval' is not assignable to parameter of type 'never'.
                newIntervals.push(interval);
            }
            return newIntervals;
        },
        [],
        intervals
    );
};

export const MAX_RESOURCE_FETCH_LIMIT = 99;
const RESOURCE_FETCH_LIMIT = 25;
const TIMEUNIT_FETCH_LIMIT = 2;

const getTimeUnitFetchLimit = (timeUnit: TimeUnit, resourceRange: IntervalType) => {
    switch (timeUnit) {
        case TimeUnit.Day:
        case TimeUnit.Hour:
            return 360;
        case TimeUnit.Month: {
            const resourcesLength = resourceRange.high - resourceRange.low;
            if (resourcesLength < RESOURCE_FETCH_LIMIT) {
                return TIMEUNIT_FETCH_LIMIT * 2;
            }
            if (resourcesLength < RESOURCE_FETCH_LIMIT * 2) {
                return TIMEUNIT_FETCH_LIMIT;
            }
            return 1;
        }
        default:
            throw Error('Time unit not provided');
    }
};

export function getDividedResourceRange(resourceRange: IntervalType): IntervalType[] {
    const resourcesLength = resourceRange.high - resourceRange.low;
    if (resourcesLength > MAX_RESOURCE_FETCH_LIMIT) {
        const midPoint = resourceRange.low + MAX_RESOURCE_FETCH_LIMIT - 1;
        const firstRange: IntervalType = { low: resourceRange.low, high: midPoint };
        const secondRange: IntervalType = { low: midPoint + 1, high: resourceRange.high };
        return [firstRange, ...getDividedResourceRange(secondRange)];
    }
    return [resourceRange];
}

export const chunkBlocksByResources = (blocks: TimelineBlock[]) =>
    blocks.reduce((newBlocks: TimelineBlock[], block) => {
        const newResourceRanges = getDividedResourceRange(block.verticalRange);
        const newChunkedBlocks = newResourceRanges.map((resourceRange) => ({
            horizontalRange: block.horizontalRange,
            verticalRange: resourceRange,
        }));
        newBlocks.push(...newChunkedBlocks);
        return newBlocks;
    }, []);

export const chunkBlocksByTimeUnit = ({
    blocks,
    timeUnit,
    timezone,
    timelineTimeOrigin,
}: {
    blocks: TimelineBlock[];
    timeUnit: TimeUnit;
    timezone: string;
    timelineTimeOrigin: DateTime;
}) =>
    blocks.reduce((newBlocks: TimelineBlock[], block) => {
        const unitLimit = getTimeUnitFetchLimit(timeUnit, block.verticalRange);
        const interval = intervalToUTC(block.horizontalRange, timeUnit, timelineTimeOrigin);
        const intervalLength = getIntervalUnitLength(interval, timeUnit, timezone);
        if (intervalLength > unitLimit) {
            const newIntervals = getDividedDateRangeByTimeUnit({
                interval,
                timeUnit,
                unitAmount: unitLimit,
                timezone,
            });
            const newChunkedBlocks = newIntervals.map((newInterval) => ({
                horizontalRange: IntervalService.create({
                    low: TimelineService.utcToTimeUnitIndex(newInterval.startUtc, timezone, timeUnit),
                    high: TimelineService.utcToTimeUnitIndex(newInterval.endUtc, timezone, timeUnit),
                }),
                verticalRange: block.verticalRange,
            }));
            newBlocks.push(...newChunkedBlocks);
        } else {
            newBlocks.push(block);
        }
        return newBlocks;
    }, []);

const manipulateDays = (amount: number) => (dateTimeUtc: DateTimeUtc) =>
    DateTimeUtcService.addDays(dateTimeUtc, amount);
const manipulateMonths = (amount: number) => (dateTimeUtc: DateTimeUtc) =>
    DateTimeUtcService.addMonths(dateTimeUtc, amount);
const manipulateHours = (amount: number) => (dateTimeUtc: DateTimeUtc) =>
    DateTimeUtcService.addHours(dateTimeUtc, amount);

export const timeUnitManipulationTimeRange = createHandler({
    [TimeUnit.Month]: () => manipulateMonths,
    [TimeUnit.Day]: () => manipulateDays,
    [TimeUnit.Hour]: () => manipulateHours,
});

export const timeUnitRequestOffsets = createHandler({
    [TimeUnit.Hour]: { startPaddingFunction: manipulateHours(-2), endPaddingFunction: manipulateHours(2) },
    [TimeUnit.Month]: { startPaddingFunction: manipulateDays(-2), endPaddingFunction: manipulateDays(2) },
    [TimeUnit.Day]: {
        startPaddingFunction: manipulateDays(-1),
        endPaddingFunction: manipulateDays(1),
    },
});

export const sortResourcesByPosition = (resources: Resource[]) =>
    [...resources].sort((a, b) => {
        if (a.minY < b.minY) return -1;
        if (a.minY > b.minY) return 1;
        if (a.minX < b.minX) return -1;
        if (a.minX > b.minX) return 1;
        return 0;
    });
